import {Prop, Vue, Watch} from 'vue-property-decorator';
import Component from 'vue-class-component';
import {Validations} from 'vuelidate-property-decorators';
import {AutocompleteOptions} from '@/interfaces/ed-autocomplete-options';

@Component
class EdAutocomplete extends Vue {
  @Prop({
    type: Array,
    required: true,
    validator: (items: any[]) => items.every(item => 'value' in item && 'text' in item),
  }) items!: AutocompleteOptions[];
  @Prop({type: String, default: ''}) readonly label!: string;
  @Prop({type: Number, default: 0}) readonly rowMessagesSupport!: number;
  @Prop() readonly hint!: string[];
  @Prop({type: String, default: 'rgba(0, 0, 0, 0.10)'}) backgroundItemActive!: string;
  @Prop({type: String, default: 'rgba(0, 0, 0, 0.87)'}) colorItemActive!: string;
  @Prop({type: Boolean, default: false}) readonly required: boolean;
  @Prop({type: Boolean, default: false}) readonly disabled!: boolean;
  @Prop({type: Boolean, default: false}) readonly multiple!: boolean;
  @Prop({type: Boolean, default: false}) readonly clearable!: boolean;
  @Prop({type: Boolean, default: false}) readonly deactivateAnimation!: boolean;
  @Prop({type: Boolean, default: false}) readonly fixedItemList!: boolean;
  @Prop({type: Boolean, default: false}) readonly isLoading!: boolean;
  @Prop({type: Number, default: 1}) readonly opacity!: number;


  text = '';
  selectedItems: AutocompleteOptions[] = [];
  selectedValue: string | number = '';
  selectedItem: AutocompleteOptions = null;
  results: AutocompleteOptions[] = [];
  showResults = false;
  activeIndex = null;
  isFocused = false;
  isInteracting = false;
  isKeydown = false;
  maintainFocus = false;
  isLookingMatch = false;
  isOnlyCoincidenceItem = false;
  isKeydownEnter = false;
  isRemoveAllItems = false;
  currentCursorColor = 'transparent';

  @Validations()
  validations() {
    const attrs: any = this.$attrs;
    return {
      value: attrs?.value?.validators ?? {}
    };
  }

  get validators() {
    let input: any = this.$attrs.value;
    return input ? input.validators : {};
  }

  get configStyles() {
    return {
      '--ed-indicator-color': this.isInvalid ? 'var(--ed-color-danger)' : 'var(--ed-color-green)',
      '--ed-counter-color': this.isInvalid ? 'var(--ed-color-danger)' : 'var(--ed-color-medium)',
      '--ed-border-color-active': this.isInvalid ? 'var(--ed-color-danger)' : this.borderColor,
      '--ed-label-transition': this.deactivateAnimation ? 'none' : 'all 0.3s ease',
      '--ed-color-item-selected': this.colorItemActive,
      '--ed-color-item-selected-background': this.isOnlyCoincidenceItem? 'rgba(0, 0, 0, 0.10)' : this.backgroundItemActive,
      '--ed-color-opacity-item-selected': this.isOnlyCoincidenceItem? 1 : this.opacity ,
      '--ed-color-cursor': this.cursorColor ,
    };
  }

  applyCursorColor() {
    setTimeout(() => {
      if (this.isInvalid) {
        this.currentCursorColor  ='var(--ed-color-danger)';
      } else if (this.isFocused) {
        this.currentCursorColor  ='var(--ed-color-green)';
      } else {
        this.currentCursorColor  ='transparent';
      }
    }, 100);
  }

  get cursorColor() {
      return this.currentCursorColor;
  }

  get classComputed() {
    return {
      'position-fixed': this.fixedItemList,
      'position-absolute': !this.fixedItemList
    };
  }

  isOnlyCoincidence(result: AutocompleteOptions) {
    this.isOnlyCoincidenceItem = (result.text.toLowerCase() === this.text.toLowerCase() && this.results.length == 1);
    return (result.text.toLowerCase() === this.text.toLowerCase() && this.results.length == 1);
  }

  get isInvalid() {
    return this.$v.value.$invalid;
  }

  get borderColor() {
    return this.isFocused ? 'var(--ed-color-green)' : 'var(--ed-color-medium)';
  }

  get isEmptyList(): boolean {
    return this.results.length === 0;
  }

  get isMultiple(): boolean {
    return this.isFocused && this.multiple;
  }

  get errorMessages(): string[] {
    const messages: string[] = [];
    if (this.isRequiredError) {
      messages.push(this.$i18n.t('errors.required').toString());
    }
    return messages;
  }

  get isRequiredError() {
    if (this.$v.value.$params.required) {
      return !this.$v.value.required;
    }

    return false;
  }

  highlightText(item: AutocompleteOptions): string {
    if (!this.text || !this.isKeydown && !this.isFocused || !this.isLookingMatch || this.isKeydownEnter) return item.text;

    if (this.selectedValue == item.value && this.maintainFocus && !this.isFocused) return item.text;

    const regex = new RegExp(`(${this.text})`, 'gi');

    if (this.selectedValue == item.value) {
      return item.text.replace(regex, '<span style="cursor: pointer; background-color: #d5d5d5; color: #838383">$1</span>');
    }

    return item.text.replace(regex, '<span style="cursor: pointer; background-color: #eeeeee; color: rgba(0, 0, 0, .38)">$1</span>');
  }

  removeAllItems() {
    this.isInteracting = this.isFocused;

    this.selectedItems = [];
    this.isRemoveAllItems = true;
    this.results = this.items;
  }

  moveCursorToEnd(element) {
    const range = document.createRange();
    const selection = window.getSelection();

    range.selectNodeContents(element);
    range.collapse(false);

    selection.removeAllRanges();
    selection.addRange(range);
  }

  onInputEditable(valueInput): void {
    const refsEditableText = this.$refs.editableText  as HTMLElement;
    const isOnlyNbsp = refsEditableText.innerHTML === '&nbsp;';
    const isEmpty = refsEditableText.innerText.trim() === '';
    let isDeleting = valueInput.inputType === 'deleteContentBackward' || valueInput.inputType === 'Delete';

    if (isDeleting && (isOnlyNbsp || isEmpty)) {
      this.deleteItemInput(isDeleting);
    }

    const editableText = valueInput.target;

    if (editableText) {
      this.text = editableText.textContent?.trim() || '';
    }

    if (isDeleting) {
      this.handleEmptyContent();
    }

    this.updateResults(true);
  }

  handleEmptyContent() {
    const editableText = this.$refs.editableText  as HTMLElement;
    const isEmpty = editableText.innerText.trim() === '';

    if (isEmpty) {
      editableText.innerHTML = '&nbsp;';
      this.moveCursorToEnd(editableText);
    }
  }

  onInput(valueInput): void {
    this.isKeydownEnter = false;
    this.isLookingMatch = this.selectedItem?.text != this.text;
    this.updateResults(this.isLookingMatch);
  }

  updateResults(isSearched: boolean = false): void {
    const searchTerm = isSearched ? this.text.trim().toLowerCase() : '';
    this.results = this.items.filter(item => item.text.toLowerCase().includes(searchTerm));
    this.showResults = true;
  }

  onClickSelectResult(result: AutocompleteOptions) {
    this.selectResult(result, this.updateResults);
  }

  selectResult(result: AutocompleteOptions, updateResults?: Function) {
    this.text = result.text;
    this.selectedValue = result.value;
    this.selectedItem = result;
    this.showResults = false;
    this.activeIndex = this.items.indexOf(result);

    if (updateResults) {
      updateResults();
    }

    this.emitInput(result);
  }

  onClick() {
    if (!this.isFocused || this.results.length == 1) {
      this.updateResults();
      this.isKeydown = false;
    }

    this.isInteracting = this.selectedItems.length > 0;
    this.isInteracting = true;
    this.isRemoveAllItems = false;
  }

  handleOnClickMultiselect() {
    if (this.selectedItems.length > 0) {
      this.handleEmptyContent();
    }
  }

  private textIsNull() {
    return this.text == null || this.text == '' || this.text.length == 0;
  }

  keyupDown(event) {
    this.isKeydown = true;
    this.isRemoveAllItems = false;
  }

  deleteItemInput(isDeleting: boolean = false): void {
    let textEmpty = this.textIsNull();
    if (this.multiple && textEmpty && isDeleting) {
        this.selectedItems.pop();
    }
  }

  onEnter() {
    let isMatchValue = this.results.length == 1;
    let item = isMatchValue
      ? this.results[0]
      : this.items.find(item => item.value === this.selectedValue);

    if (item && isMatchValue) {
      this.selectResult(item);
      this.isInteracting = false;
      this.isKeydownEnter = true;
    }

    this.$emit('enter');
  }

  onEnterEditable(event) {
    event.preventDefault();
    let isMatchValueEditable = this.results.length == 1;
    if (isMatchValueEditable) {
      const item = this.results[0];
      const index = this.selectedItems.findIndex(element => element === item);


      if (index == -1) {
        this.selectedItems.push(item);
      }

      if (index !== -1) {
        let selectedItems = this.selectedItems.filter((selected) => selected.value !== item.value);
        this.selectedItems = [];
        this.$emit('input', selectedItems);

        this.$nextTick(() => {
          this.$set(this, 'selectedItems', [...selectedItems]);
          this.$forceUpdate();
        });
      }

      this.isInteracting = false;
      this.isKeydownEnter = true;
    }

    this.$emit('enter');
  }

  setFocus() {
    this.isLookingMatch = this.selectedItem?.text != this.text;

    if (this.isLookingMatch && this.maintainFocus && this.isKeydown) {
      this.updateResults(true);
    }

    setTimeout(() => {
      this.isFocused = true;
      if (this.text.length === 0 && !this.isRemoveAllItems) {
        this.updateResults();
      }
      this.maintainFocus = false;
      this.applyCursorColor();
    }, 150);

    this.handleOnClickMultiselect();
    this.$emit('focus');
  }

  setBlur() {
    this.isKeydown = false;

    setTimeout(() => {
      if (!this.maintainFocus) {
        this.isFocused = false;
        this.showResults = false;
        this.text = '';
      } else if (this.isLookingMatch && !this.isKeydownEnter && !this.isRemoveAllItems) {
        this.updateResults(true);
      }

      if (this.isInteracting) {
        this.updateFocusEditableText(true);
        this.isInteracting = false;
      }

      this.setValueBlur();
    }, 150);

    this.currentCursorColor  ='transparent';
    this.$emit('blur');
  }

  setBlurEditableText() {
    this.isKeydown = false;
    setTimeout(() => {
      if (this.isInteracting && this.multiple || this.maintainFocus) {
        this.updateFocusEditableText(true);
        this.isInteracting = false;
      } else {
        this.isFocused = false;
        this.showResults = false;
      }
    }, 150);


    this.currentCursorColor  ='transparent';
    this.$emit('blur');
  }

  setValueBlur() {
    if (this.activeIndex != null && !this.maintainFocus) {
      this.emitInput(this.items[this.activeIndex]);
    }
  }

  onWindowBlur() {
    if (this.isInteracting || this.isFocused) {
      this.maintainFocus = true;
      return;
    }
  }

  private emitInput(result) {
    if (result == undefined) {
      return;
    }

    this.$emit('input', {
      value: result.value,
      valid: !this.$v.value.$invalid,
      validators: this.validators,
    });
  }

  updateFocusEditableText(updateResults: boolean = false) {
    const editableText = this.$refs.editableText as HTMLElement;
    if (editableText) {
      editableText.focus();
      this.isFocused = true;

      if (updateResults) {
        this.updateResults();
      }
      this.$emit('focus');
    }
  }

  updateEditableInputWidth() {
    const input = this.$refs.inputWrapper as HTMLElement;
    const editableInput = this.$refs.editableInput as HTMLElement;

    if (input && editableInput) {
      const inputRect = input.getBoundingClientRect();
      editableInput.style.width = `${inputRect.width}px`;
    }
  }

  toggleSelectItem(item: AutocompleteOptions): void {
    this.isInteracting = true;
    const exists = this.selectedItems.some((selected) => selected.value === item.value);

    if (exists) {
      this.selectedItems = this.selectedItems.filter((selected) => selected.value !== item.value);
    } else {
      this.selectedItems.push(item);
    }

    this.toggleInputEdiText();
    this.handleEmptyContent();
    this.$emit('input', this.selectedItems);
  }

  toggleInputEdiText() {

    if (this.selectedItems.length == 1) {
      setTimeout(() => {
        this.updateFocusEditableText();
      }, 100);
    }
  }

  removeItem(item: AutocompleteOptions): void {
    this.selectedItems = this.selectedItems.filter((selected) => selected.value !== item.value);
    this.$emit('input', this.selectedItems);
  }

  isSelected(item: AutocompleteOptions) {
    return this.selectedItems.some(i => i.value === item.value);
  }

  updateDropdownList() {
    this.$nextTick(() => {
      const input = this.$refs.inputWrapper as HTMLElement;
      const autocompleteDropdown = this.$refs.autocompleteDropdown as HTMLElement;
      if (autocompleteDropdown) {
        const inputRect = input.getBoundingClientRect();
        autocompleteDropdown.style.width = `${inputRect.width}px`;
      }
    });
  }

  validateValueSelected() {
    const selectedItem = this.items.find(item => item.value === this.text);

    if (selectedItem) {
      this.text = selectedItem.text;
      this.selectedValue = selectedItem?.value;
    }
  }


  mounted() {
    if (this.fixedItemList) {
      this.updateDropdownList();
    }

    this.updateEditableInputWidth();
    window.addEventListener('blur', this.onWindowBlur);

    this.validateValueSelected();
  }

  beforeDestroy() {
    window.removeEventListener('blur', this.onWindowBlur);
  }

  @Watch('$attrs')
  attrsChanged() {
    if (this.isLoading) return;

    const input = this.$attrs?.value;
    if (Array.isArray(input) && input.length > 0) {

      this.selectedItems = this.items.filter(item =>
        input.some(searchItem =>
          item.text === searchItem.text && item.value === searchItem.value
        )
      );
      return;
    }

    if (typeof input === 'object') {
      const newValue = (input as AutocompleteOptions).value ?? null;
      const matchedItem = this.items.find(item => item.value === newValue);
      this.activeIndex = this.items.indexOf(matchedItem);
      this.selectedValue = matchedItem?.value;
      this.selectedItem = matchedItem;

      if (matchedItem && this.text !== matchedItem.text) {
        this.text = matchedItem.text;
      } else if (!matchedItem) {
        this.text = '';
      }
    }

    this.applyCursorColor();
  }
}

export default Vue.component('ed-autocomplete', EdAutocomplete);